import React, { useEffect, useState } from "react";
import Subnav from "./Subnav";
import { RecurlyPlan } from "@/lib/recurly";
import TextLogo from "@/components/TextLogo/TextLogo";
import Flex from "@/components/Flex/Flex";
import { SwitchablePlan } from "./SwitchablePlan/SwitchablePlan";
import { useSearch } from "wouter";
import { getPlan, getPrimaryPlans } from "@/api/billing";
import { logRocketError } from "@/utils/logUtils";

import styles from "./PlanLayout.module.css";

// these are query params that will be present if this user got here from an affiliate link.
// 'click' id:
export const AFFILIATE_XID_PARAM_NAME = "ps_xid";

// this is actually unused, but leave it here for documentation (it will be in the url when
// you come from an affiliate link, and we might use it in the future. it identifies the parter)
// export const AFFILIATE_PARTNER_KEY_PARAM_NAME = "ps_partner_key";

export default function PlanLayout({ children }: { children: React.ReactNode }) {
  const searchParams = new URLSearchParams(useSearch());

  // the currently selected plan; the one the customer will checkout with.
  // additionally we store the 'primary' plans, which is just a list of
  // the plans that the user can select in the app itself. There are other
  // plans which are only accessible by being handed a 'special' link, with
  // the plan code in the url.
  const [plan, setPlan] = useState<RecurlyPlan | null>(null);
  const [primaryPlans, setPrimaryPlans] = useState<RecurlyPlan[]>([]);

  useEffect(() => {
    getPrimaryPlans().then(plans => {
      setPrimaryPlans(plans);
    });
  }, []);

  const setPlanFromCode = async (planCode: string) => {
    let recurlyPlan = primaryPlans.find(p => p.code === planCode);
    if (!recurlyPlan) {
      recurlyPlan = await getPlan(planCode);
    }
    if (!recurlyPlan) {
      return false;
    }

    const params = new URLSearchParams(window.location.search);
    params.set("plan", recurlyPlan.code);
    // replaceState so we don't re-load the page or break back/forward
    window.history.replaceState(null, "", "?" + params.toString()); 

    setPlan(recurlyPlan);
    return true;
  }

  useEffect(() => {
    // if we have a url param, but no Recurly plan, go and load it:
    const urlPlanCode = searchParams.get("plan") ?? "standard-monthly-launchspecial";
    if (!plan && !setPlanFromCode(urlPlanCode)) {
      // if we failed to set the plan from the code in the url, the plan-code in the url is invalid.
      // unset it:
      searchParams.delete("plan");
      logRocketError(`Failed to find plan with code: |${urlPlanCode}|`);
    }
  }, [searchParams])

  return (
    <div className={styles.container}>
      <main className={styles.main}>
        <div className={styles.header}>
          <div className={styles.logo}>
            <TextLogo />
          </div>
          <Subnav />
        </div>

        {children}

        <Flex gap={2} justify="center" style={{marginTop:"2rem"}}>
          <span className="small-text">© Boxem</span>
          <span className="small-text">
            <a href="https://www.boxem.com/privacypolicy" target="_blank">Privacy</a>
          </span>
          <span className="small-text">
            <a href="https://www.boxem.com/termsandconditions" target="_blank">Terms</a>
          </span>
        </Flex>
      </main>
      <aside className={styles.aside}>
        <SwitchablePlan
          plan={plan}
          setPlanFromCode={setPlanFromCode}
        />
      </aside>
    </div>
  );
}

// const findPlan = (param: string) => {
//   const possiblePlans = [
//     "launch-special-monthly",
//     "launch-special-annual",
//     "professional-997-webinaroffer",
//     "professional-launch-annual-inapp",
//     "professional-launch-monthly-inapp",
//     "standard-launch-annual-inapp",
//     "standard-launch-monthly-inapp",

//     "standard-monthly-launchspecial",
//     "standard-annual-launchspecial",

//     "professional-monthly-launchspecial",
//     "professional-annual-launchspecial",
//     "standard-webinaroffer",
//     "professional-webinaroffer",
//     "professional-roadmap-annual",
//     "professional-roadmap-monthly", 
//     "standard-roadmap-annual",
//     "standard-roadmap-monthly",

//     // go aura integration:
//     "professional-monthly-integration",
//     "professional-annual-integration",
//     "standard-monthly-integration",
//     "standard-annual-integration",
//   ];
// };
